import type { JSX } from 'react'

import { Card, Flex, Heading, Skeleton, Text } from '@buffer-mono/popcorn'

import type { TeamMemberCounts } from '../../hooks'
import {
  COLLABORATION_ROLE_LABELS,
  COLLABORATION_ROLES,
} from '../../utils/collaborationRoles'

import styles from './TeamSummaryCards.module.css'

type SummaryCard = {
  label: string
  count: number | undefined
  limit: number | null | undefined
}

type TeamSummaryCardsProps = {
  /** Organization-wide counts, or null while they are loading. */
  counts: TeamMemberCounts | null
}

export const TeamSummaryCards = ({
  counts,
}: TeamSummaryCardsProps): JSX.Element => {
  const cards: readonly SummaryCard[] = [
    { label: 'Team members', count: counts?.total, limit: counts?.limit },
    ...COLLABORATION_ROLES.map((role) => {
      const roleCount = counts?.byRole.find((entry) => entry.role === role)
      return {
        label: COLLABORATION_ROLE_LABELS[role],
        // A role missing from a loaded response has no members.
        count: counts ? (roleCount?.count ?? 0) : undefined,
        limit: roleCount?.limit,
      }
    }),
  ]

  return (
    <Flex gap="sm" wrap="wrap" fullWidth>
      {cards.map(({ label, count, limit }) => (
        <Card key={label} padding="none" className={styles.card}>
          <Text as="p" size="sm" weight="medium" color="subtle">
            {label}
          </Text>
          <Flex align="baseline" gap="2xs">
            {count === undefined ? (
              <Skeleton width={20} height={24} />
            ) : (
              <>
                <Heading as="p" size="lg">
                  {count}
                </Heading>
                <Text size="sm" color="subtle">
                  /{limit ?? 'unlimited'}
                </Text>
              </>
            )}
          </Flex>
        </Card>
      ))}
    </Flex>
  )
}
